// pages/BlogPage.js
import React, { useState } from 'react';
import { Routes, Route, useParams, Link, useLocation } from 'react-router-dom';
import MarkdownRenderer from '../components/blog/MarkdownRenderer';
import blogData from '../data/blog/posts.json';
import styles from './BlogPage.module.css';

// Drugi sloj - "Podmeni" sa naslovima blogova (sivi kao kod KSB)
const BlogMenu = ({ onShowAll, showAll }) => {
  const location = useLocation();
  
  const publishedPosts = blogData.posts.filter(post => post.published);
  const sortedBlogs = [...publishedPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
  const latestBlogs = sortedBlogs.slice(0, 3); // Prva 3 najnovija
  
  return (
    <nav className={styles.subMenuLayer}>
      <div className={styles.contentContainer}>
        <ul className={styles.subMenuList}>
          {/* Prva 3 bloga */}
          {latestBlogs.map((blog) => (
            <li key={blog.id}>
              <Link 
                to={`/blog/${blog.slug}`}
                className={`${styles.subMenuLink} ${
                  location.pathname === `/blog/${blog.slug}` ? styles.activeSub : ''
                }`}
              >
                {blog.title.length > 30 ? blog.title.substring(0, 30) + '...' : blog.title}
              </Link>
            </li>
          ))}
          
          {/* Dugme za prikaz svih blogova */}
          <li>
            <button 
              onClick={onShowAll}
              className={`${styles.subMenuLink} ${styles.showAllButton} ${
                showAll ? styles.activeSub : ''
              }`}
            >
              Učitaj više
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

// Treći sloj - Prikaz poslednjeg bloga
const LatestBlogContent = () => {
  const publishedPosts = blogData.posts.filter(post => post.published);
  const sortedBlogs = [...publishedPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
  const latestBlog = sortedBlogs[0]; // Najnoviji blog
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };
  
  if (!latestBlog) {
    return (
      <div className={styles.pageContent}>
        <div className={styles.contentContainer}>
          <p>Još nema blogova.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.pageContent}>
      <div className={styles.contentContainer}>
        <article className={styles.blogPost}>
          <header className={styles.blogPostHeader}>
            <div className={styles.blogPostMeta}>
              <span className={styles.blogPostDate}>
                {formatDate(latestBlog.date)}
              </span>
            </div>
            <h1 className={styles.blogPostTitle}>{latestBlog.title}</h1>
            <p className={styles.blogPostExcerpt}>{latestBlog.excerpt}</p>
          </header>
          
          {latestBlog.image && (
            <div className={styles.blogPostImage}>
              <img src={latestBlog.image} alt={latestBlog.title} />
            </div>
          )}
          
          <div className={styles.blogPostContent}>
            <MarkdownRenderer content={latestBlog.content} />
          </div>
        </article>
      </div>
    </div>
  );
};

// Treći sloj - Prikaz svih blogova
const AllBlogsContent = () => {
  const [visiblePosts, setVisiblePosts] = useState(6);
  
  const publishedPosts = blogData.posts.filter(post => post.published);
  const sortedBlogs = [...publishedPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
  const postsToShow = sortedBlogs.slice(0, visiblePosts);
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };
  
  const loadMore = () => {
    setVisiblePosts(prev => prev + 6);
  };
  
  return (
    <div className={styles.pageContent}>
      <div className={styles.contentContainer}>
        <p className={styles.pageDescription}>
          Svi tekstovi i praktični savjeti za prodaju kompanije
        </p>
        
        <div className={styles.blogGrid}>
          {postsToShow.map((blog) => (
            <Link 
              to={`/blog/${blog.slug}`}
              key={blog.id}
              className={styles.blogCard}
            >
              {blog.image && (
                <div className={styles.blogCardImage}>
                  <img src={blog.image} alt={blog.title} />
                </div>
              )}
              <div className={styles.blogCardContent}>
                <div className={styles.blogCardMeta}>
                  <span className={styles.blogCardDate}>
                    {formatDate(blog.date)}
                  </span>
                </div>
                <h2 className={styles.blogCardTitle}>{blog.title}</h2>
                <p className={styles.blogCardExcerpt}>{blog.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
        
        {visiblePosts < sortedBlogs.length && (
          <div className={styles.loadMoreContainer}>
            <button onClick={loadMore} className={styles.loadMoreButton}>
              Učitaj još
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Post komponenta (pojedinačni tekst)
const BlogPostContent = () => {
  const { slug } = useParams();
  
  const blogPost = blogData.posts.find(post => 
    post.slug === slug && post.published
  );
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };
  
  if (!blogPost) {
    return (
      <div className={styles.pageContent}>
        <div className={styles.contentContainer}>
          <h1>Blog post nije pronađen</h1>
          <p>Traženi blog post ne postoji.</p>
          <Link to="/blog" className={styles.backButton}>
            ← Povratak na blog
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.pageContent}>
      <div className={styles.contentContainer}>
        <article className={styles.blogPost}>
          <header className={styles.blogPostHeader}>
            <div className={styles.blogPostMeta}>
              <span className={styles.blogPostDate}>
                {formatDate(blogPost.date)}
              </span>
            </div>
            <h1 className={styles.blogPostTitle}>{blogPost.title}</h1>
            <p className={styles.blogPostExcerpt}>{blogPost.excerpt}</p>
          </header>
          
          {blogPost.image && (
            <div className={styles.blogPostImage}>
              <img src={blogPost.image} alt={blogPost.title} />
            </div>
          )}
          
          <div className={styles.blogPostContent}>
            <MarkdownRenderer content={blogPost.content} />
          </div>
        </article>
      </div>
    </div>
  );
};

// Glavna BlogPage komponenta
const BlogPage = () => {
  const [showAllBlogs, setShowAllBlogs] = useState(false);
  
  return (
    <div className={styles.wrapper}>
      {/* Prvi sloj - Naslov (sivi) */}
      <div className={styles.titleLayer}>
        <div className={styles.contentContainer}>
          <h1 className={styles.pageTitle}>Blog</h1>
        </div>
      </div>
      
      {/* Drugi sloj - Podmeni (sivi) */}
      <BlogMenu onShowAll={() => setShowAllBlogs(true)} showAll={showAllBlogs} />
      
      {/* Treći sloj - Sadržaj */}
      <Routes>
        <Route path="/" element={
          showAllBlogs ? <AllBlogsContent /> : <LatestBlogContent />
        } />
        <Route path=":slug" element={<BlogPostContent />} />
      </Routes>
    </div>
  );
};

export default BlogPage;

